import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  Plug,
  Layers,
  Plus,
  ArrowLeftRight,
  ShieldCheck,
  RefreshCw,
  Camera,
  Inbox,
  Wand2,
  Slack,
  Send,
  Sparkles,
  Ticket,
  Calendar,
  Lock,
  FlaskConical,
  FolderGit2,
  GitBranch,
  Workflow as WorkflowIcon,
  Rocket,
  Pause,
  LayoutTemplate,
  History,
  HeartPulse,
  Activity,
  UserCheck,
  RotateCcw,
  Ban,
  XCircle,
  PlayCircle,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useWorkflow } from "@/store/workflowStore";
import { useExecution } from "@/store/executionStore";
import { APP_LIST } from "@/data/apps";
import { useAuth } from "@/auth/AuthProvider";
import { rehydrateOrgData } from "@/lib/orgs";
import { appState, isEnabled } from "@/lib/entitlements";
import type { AppId } from "@/types";
import type { LucideIcon } from "lucide-react";

interface Item {
  id: string;
  label: string;
  hint: string;
  group: string;
  icon: LucideIcon;
  color?: string;
  run: () => void;
}

export function CommandPalette() {
  const {
    paletteOpen,
    setPalette,
    openApp,
    toggleTheme,
    setWsWizard,
    workspaceAction,
    activeWorkspaceId,
    setActiveWorkspace,
    selectWorkspace,
    codeWorkspaces,
    selectIntegration,
    connectIntegration,
    refineTask,
    selectedTaskId,
    autoResolveLevel,
    setAutoResolveLevel,
    pushToast,
    integrations,
    entitlements,
  } = useHive();
  const { me, switchOrg } = useAuth();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // global hotkey
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(!useHive.getState().paletteOpen);
      }
      if (e.key === "Escape") setPalette(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPalette]);

  useEffect(() => {
    if (paletteOpen) {
      setQ("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [paletteOpen]);

  const items: Item[] = useMemo(() => {
    const acc: Item[] = [];
    const aresEnabled = isEnabled(entitlements, "ares");
    APP_LIST.forEach((a) => {
      // Hidden products never surface; gated ones are labelled honestly (the
      // click still routes to the preview surface via openApp).
      const state = appState(entitlements, a.id);
      if (state === "hidden") return;
      acc.push({
        id: `app-${a.id}`,
        label: a.name,
        hint: state === "enabled" ? a.tagline : `${a.tagline} · Early Access`,
        group: "Applications",
        icon: a.icon,
        color: a.accent,
        run: () => openApp(a.id as AppId),
      });
    });

    // Explicit commands (the brief's Cmd+K verbs)
    const cmds: Omit<Item, "group">[] = [
      // Workspace commands
      { id: "cmd-ws-new", label: "Create new workspace", hint: "Workspace · wizard", icon: Plus, color: "#14b8a6", run: () => { openApp("workspaces"); setWsWizard(true); } },
      { id: "cmd-ws-switch", label: "Switch workspace", hint: "Workspace", icon: ArrowLeftRight, color: "#14b8a6", run: () => openApp("workspaces") },
      ...(aresEnabled
        ? [{ id: "cmd-ws-verify", label: "Verify active workspace", hint: "Workspace · ARES", icon: ShieldCheck, color: "#14b8a6", run: () => { workspaceAction(activeWorkspaceId, "verify"); openApp("workspaces"); } }]
        : []),
      { id: "cmd-ws-sync", label: "Sync branch", hint: "Workspace", icon: RefreshCw, color: "#14b8a6", run: () => workspaceAction(activeWorkspaceId, "sync") },
      { id: "cmd-ws-snap", label: "Restore snapshot", hint: "Workspace", icon: Camera, color: "#14b8a6", run: () => { selectWorkspace(activeWorkspaceId); openApp("workspaces"); } },
      // Task commands
      { id: "cmd-task-queue", label: "Open task queue", hint: "Tasks", icon: Inbox, color: "#f97316", run: () => openApp("tasks") },
      { id: "cmd-task-refine", label: "Refine selected task", hint: "Tasks", icon: Wand2, color: "#f97316", run: () => { if (selectedTaskId) refineTask(selectedTaskId); openApp("tasks"); } },
      { id: "cmd-task-slack", label: "Ask follow-up via Slack", hint: "Tasks · Slack", icon: Slack, color: "#e01e5a", run: () => openApp("tasks") },
      ...(aresEnabled
        ? [{ id: "cmd-task-ares", label: "Send task to ARES", hint: "Tasks · ARES", icon: Send, color: "#f59e0b", run: () => openApp("tasks") }]
        : []),
      { id: "cmd-task-auto", label: "Toggle auto-resolve", hint: `Tasks · level ${autoResolveLevel}`, icon: Sparkles, color: "#f97316", run: () => { setAutoResolveLevel(autoResolveLevel >= 4 ? 0 : autoResolveLevel + 1); openApp("tasks"); } },
      // Integration commands
      { id: "cmd-int-slack", label: "Connect Slack", hint: "Integrations", icon: Slack, color: "#e01e5a", run: () => { connectIntegration("slack"); selectIntegration("slack"); openApp("integrations"); } },
      { id: "cmd-int-jira", label: "Connect Jira", hint: "Integrations", icon: Ticket, color: "#2684ff", run: () => { connectIntegration("jira"); selectIntegration("jira"); openApp("integrations"); } },
      { id: "cmd-int-cal", label: "Connect Google Calendar", hint: "Integrations", icon: Calendar, color: "#4285f4", run: () => { connectIntegration("gcal"); selectIntegration("gcal"); openApp("integrations"); } },
      { id: "cmd-int-admin", label: "Request admin integration", hint: "Integrations · admin", icon: Lock, color: "#a855f7", run: () => openApp("integrations") },
      { id: "cmd-int-test", label: "Test integration health", hint: "Integrations", icon: FlaskConical, color: "#06b6d4", run: () => { pushToast({ title: "Health check passed", body: "All connected integrations responded", tone: "success" }); openApp("integrations"); } },
    ];
    cmds.forEach((c) => acc.push({ ...c, group: "Commands" }));

    // Workflow Builder commands
    const wfCmds: Omit<Item, "group">[] = [
      { id: "wf-create", label: "Create workflow", hint: "Workflow Builder", icon: Plus, color: "#f43f5e", run: () => { useWorkflow.getState().newWorkflow(); openApp("workflows"); } },
      { id: "wf-open", label: "Open Workflow Builder", hint: "Hook → Process → Publish", icon: WorkflowIcon, color: "#f43f5e", run: () => openApp("workflows") },
      { id: "wf-template", label: "Use workflow template", hint: "Workflow Builder", icon: LayoutTemplate, color: "#f43f5e", run: () => { openApp("workflows"); useWorkflow.getState().setTemplatesOpen(true); } },
      { id: "wf-test", label: "Run workflow test", hint: "Sandbox simulation", icon: FlaskConical, color: "#f43f5e", run: () => { openApp("workflows"); useWorkflow.getState().fireTest(); } },
      { id: "wf-publish", label: "Publish workflow", hint: "Workflow Builder", icon: Rocket, color: "#f43f5e", run: () => { openApp("workflows"); const id = useWorkflow.getState().selectedWorkflowId; if (id) useWorkflow.getState().requestPublish(id); } },
      { id: "wf-pause", label: "Pause workflow", hint: "Workflow Builder", icon: Pause, color: "#f43f5e", run: () => { const id = useWorkflow.getState().selectedWorkflowId; if (id) useWorkflow.getState().pauseWorkflow(id); } },
      { id: "wf-runs", label: "View workflow runs", hint: "Execution Center", icon: History, color: "#22d3ee", run: () => { const id = useWorkflow.getState().selectedWorkflowId; useExecution.getState().setWorkflowFilter(id || null); openApp("execution"); } },
    ];
    wfCmds.forEach((c) => acc.push({ ...c, group: "Workflows" }));

    // Execution Center commands
    const exCmds: Omit<Item, "group">[] = [
      { id: "ex-open", label: "Open Execution Center", hint: "Live heartbeat", icon: HeartPulse, color: "#22d3ee", run: () => { useExecution.getState().setCenterView("executions"); openApp("execution"); } },
      { id: "ex-running", label: "Show running executions", hint: "Execution Center", icon: Activity, color: "#22d3ee", run: () => { useExecution.getState().setCenterView("executions"); useExecution.getState().setStatusFilter("running"); openApp("execution"); } },
      { id: "ex-failed", label: "Show failed executions", hint: "Execution Center", icon: XCircle, color: "#f43f5e", run: () => { useExecution.getState().setCenterView("executions"); useExecution.getState().setStatusFilter("failed"); openApp("execution"); } },
      { id: "ex-approvals", label: "Show approval queue", hint: "Execution Center", icon: UserCheck, color: "#a855f7", run: () => { useExecution.getState().setCenterView("approvals"); openApp("execution"); } },
      { id: "ex-approve", label: "Approve selected action", hint: "Approval queue", icon: UserCheck, color: "#22c55e", run: () => { const a = useExecution.getState().approvals.find((x) => x.status === "pending"); if (a) useExecution.getState().approve(a.id); openApp("execution"); } },
      { id: "ex-retry", label: "Retry failed step", hint: "Execution Center", icon: RotateCcw, color: "#f59e0b", run: () => { const e = useExecution.getState().executions.find((x) => x.id === useExecution.getState().selectedExecutionId); const f = e?.steps.find((s) => s.status === "failed"); if (e && f) useExecution.getState().retryStep(e.id, f.id); openApp("execution"); } },
      { id: "ex-cancel", label: "Cancel execution", hint: "Execution Center", icon: Ban, color: "#f43f5e", run: () => { const id = useExecution.getState().selectedExecutionId; if (id) useExecution.getState().cancelExecution(id); openApp("execution"); } },
      { id: "ex-ares", label: "Open active ARES run", hint: "Execution Center", icon: PlayCircle, color: "#f59e0b", run: () => { const e = useExecution.getState().executions.find((x) => x.status === "running" && (x.agent ?? "").includes("ARES")) ?? useExecution.getState().executions.find((x) => x.status === "running"); if (e) useExecution.getState().selectExecution(e.id); useExecution.getState().setCenterView("executions"); openApp("execution"); } },
    ];
    exCmds.forEach((c) => acc.push({ ...c, group: "Execution" }));

    codeWorkspaces.forEach((w) =>
      acc.push({
        id: `cws-${w.id}`,
        label: `Open ${w.name}`,
        hint: `${w.repo} · ${w.branch}`,
        group: "Workspaces",
        icon: GitBranch,
        color: w.accent,
        run: () => { setActiveWorkspace(w.id); selectWorkspace(w.id); openApp("workspaces"); },
      })
    );
    me?.orgs.forEach((o) =>
      acc.push({
        id: `org-${o.id}`,
        label: `Switch organization · ${o.name}`,
        hint: o.id === me.activeOrgId ? "Current organization" : `Your role: ${o.role}`,
        group: "Organizations",
        icon: Layers,
        color: "#f59e0b",
        run: () => {
          if (o.id === me.activeOrgId) return;
          switchOrg(o.id)
            .then(() => rehydrateOrgData())
            .catch(() => pushToast({ title: "Switch failed", body: "Couldn't switch organization — retry", tone: "error" }));
        },
      })
    );
    integrations.forEach((i) =>
      acc.push({
        id: `int-${i.id}`,
        label: i.name,
        hint: `${i.category} · ${i.status.replace(/-/g, " ")}`,
        group: "Integrations",
        icon: i.admin ? Lock : Plug,
        color: i.color,
        run: () => { selectIntegration(i.id); openApp("integrations"); },
      })
    );
    acc.push({
      id: "act-theme",
      label: "Toggle theme",
      hint: "Dark / Light",
      group: "Commands",
      icon: FolderGit2,
      run: () => toggleTheme(),
    });
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openApp, toggleTheme, activeWorkspaceId, selectedTaskId, autoResolveLevel, codeWorkspaces, integrations, me, switchOrg, entitlements]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items.slice(0, 8);
    const lower = q.toLowerCase();
    return items
      .filter(
        (i) =>
          i.label.toLowerCase().includes(lower) ||
          i.hint.toLowerCase().includes(lower) ||
          i.group.toLowerCase().includes(lower)
      )
      .slice(0, 40);
  }, [q, items]);

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    filtered.forEach((i) => {
      if (!map.has(i.group)) map.set(i.group, []);
      map.get(i.group)!.push(i);
    });
    return [...map.entries()];
  }, [filtered]);

  useEffect(() => setActive(0), [q]);

  function exec(i: Item) {
    i.run();
    setPalette(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && filtered[active]) {
      e.preventDefault();
      exec(filtered[active]);
    }
  }

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  let flatIndex = -1;

  return (
    <AnimatePresence>
      {paletteOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-[14vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setPalette(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
          >
            <div className="flex items-center gap-3 border-b border-app px-4 py-3.5">
              <Search size={18} className="text-app-2" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search apps, integrations, hooks, workspaces…"
                className="flex-1 bg-transparent text-base text-app outline-none placeholder:text-app-2"
              />
              <kbd className="rounded-md border border-app bg-surface px-1.5 py-0.5 text-[10px] text-app-2">
                esc
              </kbd>
            </div>

            <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-2">
              {grouped.length === 0 && (
                <div className="py-12 text-center text-sm text-app-2">
                  No results for “{q}”
                </div>
              )}
              {grouped.map(([group, gItems]) => (
                <div key={group} className="mb-1">
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-app-2">
                    {group}
                  </div>
                  {gItems.map((i) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    const isActive = idx === active;
                    return (
                      <button
                        key={i.id}
                        data-idx={idx}
                        onMouseMove={() => setActive(idx)}
                        onClick={() => exec(i)}
                        className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition ${
                          isActive ? "bg-surface-2" : ""
                        }`}
                      >
                        <span
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                          style={{ background: `${i.color ?? "#888"}22`, color: i.color ?? "var(--text-0)" }}
                        >
                          <i.icon size={15} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-app">{i.label}</div>
                          <div className="truncate text-xs text-app-2">{i.hint}</div>
                        </div>
                        {isActive && <CornerDownLeft size={14} className="text-app-2" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 border-t border-app px-4 py-2 text-[11px] text-app-2">
              <span className="flex items-center gap-1"><ArrowUp size={11} /><ArrowDown size={11} /> navigate</span>
              <span className="flex items-center gap-1"><CornerDownLeft size={11} /> open</span>
              <span className="ml-auto">{filtered.length} results</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
